import { unzipSync, strFromU8 } from 'fflate';
import { columnIndex, decodeEntities, parseSharedStrings } from './xlsx-reader';

/**
 * A planilha como ela é: **uma matriz crua por aba**, na posição exata de cada célula.
 *
 * `readXlsx` e `readXlsxSheets` devolvem uma tabela de texto, e isso serve a quem toma a
 * primeira linha como cabeçalho. Não serve ao cronograma que sai do GanttPRO: ele traz
 * um banner nas quatro primeiras linhas e o cabeçalho só na quinta, e quem o lê precisa
 * (a) achar o cabeçalho sozinho, (b) apontar o erro pelo **número da linha do Excel**, e
 * (c) distinguir a data — um serial com fração, `46204.333…` — do texto. Aqui nada disso é
 * decidido: o valor vem como o arquivo o guarda.
 *
 * - **A linha N está em `matrix[N - 1]`**, mesmo com linhas vazias no meio (viram `[]`);
 * - **a coluna C está em `row[2]`**, e a célula vazia é `null` (texto vazio também);
 * - número é `number`, texto é `string`, verdadeiro/falso é `boolean`. Data é número: o
 *   serial do Excel, com a hora na fração. Quem sabe que a coluna é data converte.
 *
 * Fórmula chega pelo valor que o Excel gravou junto; sem valor calculado, vem `null`.
 * Célula mesclada só tem valor no canto superior esquerdo, que é o que o arquivo guarda.
 */

export type XlsxCellValue = string | number | boolean | null;
export type XlsxMatrix = readonly (readonly XlsxCellValue[])[];

export interface XlsxMatrixSheet {
  readonly sheetName: string;
  readonly matrix: XlsxMatrix;
}

/** Todas as abas, na ordem em que o arquivo as declara. */
export function readXlsxMatrices(bytes: Uint8Array): readonly XlsxMatrixSheet[] {
  const files = unzipSync(bytes);

  const sharedXml = files['xl/sharedStrings.xml'];
  const shared = sharedXml === undefined ? [] : parseSharedStrings(strFromU8(sharedXml));

  const workbookXml = files['xl/workbook.xml'];
  const declared =
    workbookXml === undefined
      ? []
      : [...strFromU8(workbookXml).matchAll(/<sheet\b([^>]*?)\/?>/g)].map((found) => ({
          name: decodeEntities(/\bname="([^"]*)"/.exec(found[1] ?? '')?.[1] ?? ''),
          relationshipId: /\br:id="([^"]*)"/.exec(found[1] ?? '')?.[1],
        }));

  const targets = relationshipTargets(files['xl/_rels/workbook.xml.rels']);
  const byNumber = Object.keys(files)
    .filter((name) => name.startsWith('xl/worksheets/') && name.endsWith('.xml'))
    .sort((a, b) => sheetNumber(a) - sheetNumber(b));

  const paths =
    declared.length === 0
      ? byNumber
      : declared.map(
          (sheet, index) =>
            targets.get(sheet.relationshipId ?? '') ?? byNumber[index] ?? 'xl/worksheets/none.xml',
        );

  return paths.map((path, index) => {
    const xml = files[path];
    return {
      sheetName: declared[index]?.name ?? `Planilha ${String(index + 1)}`,
      matrix: xml === undefined ? [] : parseMatrix(strFromU8(xml), shared),
    };
  });
}

/**
 * Uma aba: a de nome igual ao pedido (sem distinguir caixa) ou, sem nome, a primeira.
 * `undefined` quando o arquivo não tem aba nenhuma, ou não tem a pedida.
 */
export function readXlsxMatrix(bytes: Uint8Array, sheetName?: string): XlsxMatrixSheet | undefined {
  const sheets = readXlsxMatrices(bytes);
  if (sheetName === undefined) return sheets[0];
  const wanted = sheetName.trim().toLowerCase();
  return sheets.find((sheet) => sheet.sheetName.trim().toLowerCase() === wanted);
}

/** `xl/worksheets/sheet10.xml` → 10. Sem isso, a ordenação textual põe a 10 antes da 2. */
function sheetNumber(path: string): number {
  return Number(/sheet(\d+)\.xml$/.exec(path)?.[1] ?? 0);
}

/** `rId4` → `xl/worksheets/sheet1.xml`, pelo `workbook.xml.rels`. */
function relationshipTargets(rels: Uint8Array | undefined): Map<string, string> {
  const targets = new Map<string, string>();
  if (rels === undefined) return targets;

  for (const found of strFromU8(rels).matchAll(/<Relationship\b([^>]*?)\/?>/g)) {
    const attributes = found[1] ?? '';
    const id = /\bId="([^"]*)"/.exec(attributes)?.[1];
    const target = /\bTarget="([^"]*)"/.exec(attributes)?.[1];
    if (id === undefined || target === undefined) continue;
    // O alvo é relativo a `xl/`, ou absoluto (`/xl/worksheets/sheet1.xml`).
    targets.set(id, target.startsWith('/') ? target.slice(1) : `xl/${target}`);
  }
  return targets;
}

function parseMatrix(xml: string, shared: readonly string[]): XlsxCellValue[][] {
  const matrix: XlsxCellValue[][] = [];

  // A linha vazia (`<row r="3"/>`) vem **primeiro** na alternância, pelo mesmo motivo da
  // célula: sem isso, `[^>]*` engole a barra do fecho e a busca por `</row>` atravessa a
  // linha seguinte inteira.
  const rowPattern = /<row\b([^>]*?)\/>|<row\b([^>]*)>([\s\S]*?)<\/row>/g;
  let previous = 0;
  let rowMatch: RegExpExecArray | null;

  while ((rowMatch = rowPattern.exec(xml)) !== null) {
    const attributes = rowMatch[1] ?? rowMatch[2] ?? '';
    const declared = Number(/\br="(\d+)"/.exec(attributes)?.[1] ?? '0');
    const number = declared > 0 ? declared : previous + 1;
    previous = number;

    // A linha que se fecha sozinha não tem célula: só avança a contagem. Materializá-la era
    // um `[]` por linha — e a planilha que formatou a coluna inteira traz um milhão delas
    // depois da última com dado (22/10/2026). Vindo linha com dado depois, o vão se preenche.
    if (rowMatch[1] !== undefined) continue;

    // Linhas puladas pelo arquivo existem como linhas vazias: a posição é o número.
    while (matrix.length < number) matrix.push([]);
    matrix[number - 1] = parseRow(rowMatch[3] ?? '', shared);
  }

  // As linhas vazias do fim — células formatadas sem valor — não são linha de ninguém: a
  // matriz acaba na última linha com dado. As do meio ficam, porque a posição é o número.
  while (matrix.length > 0 && matrix[matrix.length - 1]!.every((cell) => cell === null)) {
    matrix.pop();
  }

  return matrix;
}

function parseRow(xml: string, shared: readonly string[]): XlsxCellValue[] {
  const row: XlsxCellValue[] = [];
  const cellPattern = /<c\b([^>]*?)\/>|<c\b([^>]*)>([\s\S]*?)<\/c>/g;
  let cellMatch: RegExpExecArray | null;
  let previousColumn = -1;

  while ((cellMatch = cellPattern.exec(xml)) !== null) {
    const attributes = cellMatch[1] ?? cellMatch[2] ?? '';
    const body = cellMatch[3] ?? '';
    const reference = /\br="([A-Z]+)\d+"/.exec(attributes)?.[1];
    const column = reference === undefined ? previousColumn + 1 : columnIndex(reference);
    previousColumn = column;

    while (row.length <= column) row.push(null);
    row[column] = cellValue(attributes, body, shared);
  }

  return row;
}

function cellValue(attributes: string, body: string, shared: readonly string[]): XlsxCellValue {
  const type = /\bt="([^"]+)"/.exec(attributes)?.[1];
  const raw = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];

  switch (type ?? 'n') {
    case 's': {
      const index = Number(raw ?? '-1');
      return textOrNull(shared[index] ?? '');
    }
    case 'inlineStr':
      return textOrNull(
        decodeEntities(
          [...body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((match) => match[1] ?? '').join(''),
        ),
      );
    case 'str':
    case 'd':
      return textOrNull(decodeEntities(raw ?? ''));
    case 'b':
      return raw === undefined ? null : raw === '1';
    case 'e':
      return null;
    default: {
      // `n` ou sem tipo: número. Valor que não é número (`#N/A` mal tipado) vira `null`.
      if (raw === undefined || raw.trim() === '') return null;
      const value = Number(raw);
      return Number.isFinite(value) ? value : null;
    }
  }
}

function textOrNull(value: string): string | null {
  return value === '' ? null : value;
}
